import { Outlet } from 'react-router-dom';
import { FiShield, FiFlag } from 'react-icons/fi';
import { FaPlus } from 'react-icons/fa6';
import { IoIosRefresh } from 'react-icons/io';
import { toast } from 'react-toastify';
import ThreeColumnLayout from '../../layouts/ThreeColumnLayout';
import FilterSidebar from '../../organisms/FilterSidebar';
import FilterSkeleton from '../../atoms/FilterSkeleton';
import ATable from '../../organisms/ATable';
import TableSkeleton from '../../atoms/TableSkeleton';
import Loading from '../../ions/Loading';
import TextEdit from '../../molecules/TextEdit';
import PageFooter from '../../molecules/PageFooter';
import BatchStatusBadge from './shared/BatchStatusBadge';
import CreateAnalysisPageForm from './shared/CreateAnalysisPageForm';
import DraweeConfirmationFilterSidebar from './shared/DraweeConfirmationFilterSidebar';
import { formatCurrency, formatPercentage } from '../../../utils/collateralFormatters';
import useDraweeConfirmationPageList from '../../../hooks/useDraweeConfirmationPageList';

const columns = [
    { id: 'operationNumber', title: 'Número', field: 'operationNumber', canSort: true, width: '100px' },
    { id: 'op', title: 'Operação', field: 'operationCode', canSort: true, width: '120px' },
    { id: 'assignor', title: 'Cedente', field: 'assignor', canSort: true },
    { id: 'titulos', title: 'Títulos', field: 'totalReceivables', canSort: true, cssClass: 'text-center', width: '70px' },
    { id: 'sacados', title: 'Sacados', field: 'totalPayer', canSort: false, cssClass: 'text-center', width: '70px' },
    { id: 'amount', title: 'Valor', field: 'totalAmount', canSort: true, cssClass: 'text-right', width: '130px' },
    { id: 'payed', title: 'Valor Validado', field: 'validatedAmount', canSort: true, cssClass: 'text-right', width: '120px' },
    { id: 'balance', title: 'Saldo', field: 'balance', canSort: true, cssClass: 'text-right', width: '120px' },
    { id: 'percVal', title: '% Valid.', field: 'validatedPercentage', canSort: true, cssClass: 'text-right', width: '80px' },
    { id: 'status', title: 'Status', field: 'validationStatus', canSort: true, cssClass: 'text-center', width: '160px' },
];

const DraweeConfirmationPageList = () => {
    const {
        rows, total, page, setPage, pageSize,
        isLoading, initialLoading,
        apiInfo, statusCounts, priorityCounts, togglingIds,
        showNewForm, setShowNewForm,
        statusFilter, setStatusFilter,
        priorityFilter, setPriorityFilter,
        searchInput, setSearchInput, search, setSearch,
        sortField, setSortField, sortOrder, setSortOrder,
        refresh, handleRowClick, handlePriorityToggle,
    } = useDraweeConfirmationPageList();

    const filterGroups = initialLoading ? [] : [
        {
            title: '',
            children: (
                <DraweeConfirmationFilterSidebar
                    statusFilter={statusFilter}
                    priorityFilter={priorityFilter}
                    onStatusChange={setStatusFilter}
                    onPriorityChange={setPriorityFilter}
                    statusCounts={statusCounts}
                    priorityCounts={priorityCounts}
                />
            ),
        },
    ];

    return (
        <>
            {isLoading && <Loading />}

            {!showNewForm && (
                <button onClick={() => setShowNewForm(true)}
                    className="fixed top-[69px] right-[10px] w-10 h-10 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 z-50 flex items-center justify-center border-2 border-white dark:border-slate-800 bg-blue-600 hover:bg-blue-700 text-white"
                    title="Nova Análise de Validação">
                    <FaPlus size={14} />
                </button>
            )}

            <ThreeColumnLayout
                leftSidebarTitle={
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center space-x-2">
                            <FiShield className="text-blue-600" size={18} />
                            <span>Garantias</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            {apiInfo.version && <div>v{apiInfo.version}</div>}
                            <div className={`font-medium ${apiInfo.environment === 'prd' ? 'text-red-600' :
                                apiInfo.environment === 'hml' ? 'text-yellow-600' : 'text-green-600'}`}>
                                {(apiInfo.environment || '').toUpperCase()}
                            </div>
                        </div>
                    </div>
                }
                leftSidebar={
                    <FilterSidebar filterGroups={filterGroups}>
                        {initialLoading && <FilterSkeleton />}
                    </FilterSidebar>
                }
                showRightPanel={showNewForm}
                onCloseRightPanel={() => setShowNewForm(false)}
                rightPanel={showNewForm ? (
                    <div className="h-full p-6">
                        <CreateAnalysisPageForm
                            onClose={() => setShowNewForm(false)}
                            onCreated={() => { setShowNewForm(false); refresh(); toast.success('Análise criada'); }}
                        />
                    </div>
                ) : null}
            >
                <div className="flex flex-col h-full bg-white dark:bg-slate-800 relative overflow-hidden">
                    <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-800 z-10">
                        <div className="flex items-center justify-between">
                            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Validação de Sacados</h1>
                            <div className="flex items-center space-x-2">
                                <button onClick={refresh}
                                    className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                    title="Atualizar lista">
                                    <IoIosRefresh size={18} />
                                </button>
                                <div className="w-80">
                                    <TextEdit label="Buscar cessão ou cedente..." value={searchInput}
                                        onChange={e => setSearchInput(e.target.value)}
                                        onKeyDown={(e: any) => e.key === 'Enter' && setSearch(searchInput)} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 overflow-auto [&_table]:table-fixed [&_table]:w-full">
                        {initialLoading ? (
                            <TableSkeleton rows={12} />
                        ) : (
                            <ATable headers={columns} fieldSorted={sortField} orderSorted={sortOrder} onSort={(f: string, o: 'ASC' | 'DESC') => { setSortField(f); setSortOrder(o); }}>
                                {rows.length === 0 ? (
                                    <tr><td colSpan={columns.length} className="py-8 text-center text-gray-500 dark:text-gray-400">Nenhuma validação encontrada</td></tr>
                                ) : rows.map((row: any, index: number) => (
                                    <tr key={row.id}
                                        className={`group hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer ${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'}`}
                                        onClick={() => handleRowClick(row)}>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm font-medium dark:text-white">
                                            <div className="truncate" title={row.operationNumber}>{row.operationNumber || '—'}</div>
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm font-medium dark:text-white">
                                            <div className="flex items-center gap-1.5 truncate">
                                                <span className="truncate" title={row.operationCode}>{row.operationCode}</span>
                                                <button onClick={e => handlePriorityToggle(e, row)}
                                                    disabled={togglingIds.has(row.id)}
                                                    className={`ml-auto flex-shrink-0 p-0.5 rounded hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors ${row.priority ? 'text-red-500' : 'text-gray-300 opacity-0 group-hover:opacity-100'} ${togglingIds.has(row.id) ? 'opacity-30 pointer-events-none' : ''}`}
                                                    title={row.priority ? 'Remover prioridade' : 'Marcar como prioritário'}>
                                                    <FiFlag size={12} />
                                                </button>
                                            </div>
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white truncate" title={row.assignor}>{row.assignor}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center text-sm dark:text-white">{row.totalReceivables || '—'}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center text-sm dark:text-white">{row.totalPayer || '—'}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-right text-sm dark:text-white">{formatCurrency(row.totalAmount)}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-right text-sm dark:text-white">{formatCurrency(row.validatedAmount)}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-right text-sm dark:text-white">{formatCurrency(row.balance)}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-right text-sm dark:text-white">{formatPercentage(row.validatedPercentage)}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center">
                                            <BatchStatusBadge status={row.validationStatus} statusFormatted={row.validationStatusFormatted || row.validationStatus} />
                                        </td>
                                    </tr>
                                ))}
                            </ATable>
                        )}
                    </div>

                    <div className="flex-shrink-0 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                        <PageFooter currentPage={page - 1} totalPages={Math.ceil(total / pageSize)} itemsPerPage={pageSize} totalElements={total} onPageChange={(p) => setPage(p + 1)} />
                    </div>
                </div>
            </ThreeColumnLayout>

            <Outlet context={{ onDataChange: refresh }} />
        </>
    );
};

export default DraweeConfirmationPageList;
